import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../Layout/index.jsx";
import jsFileDownload from "js-file-download";
import { pdf } from "@react-pdf/renderer";
import { PDFDocument } from "../../Components/PDFDocument.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  Spinner,
  Alert,
  Modal,
  Dropdown,
  TextInput,
  Button,
} from "flowbite-react";
import ReactSelect from "react-select";
import {
  useGetClusterQuery,
  useGetAllCoursesByClusterQuery,
  useGetLeaderboardQuery,
  useGetClusterStudentsLazyQuery,
  useGetLeetcodeBulkLazyQuery,
  useSendEmailMutation,
} from "../../../../graphql/generated.tsx";
import { useParams } from "react-router-dom";
import formatDate from "../../Helpers/formatDate.jsx";
import {
  getCourseCompleted,
  getCourseTotalQuestions,
  hasCourseBreakdownData,
  escapeCsvField,
} from "../../Helpers/clusterCourseMetrics.js";
import { toast } from "../../../../toast";
import { extractLeetCodeUsername } from "../../../../utils/leetcode";
import AcSubmissionChart from "../../../Student/Components/AcSubmissionChart.jsx";
import Overlay from "../../../Student/Components/Overlay.jsx";
import StudentCourseProgression from "../../../Student/Components/StudentCourseProgression.jsx";
import { BsThreeDots } from "react-icons/bs";
import {
  MdTableChart,
  MdPictureAsPdf,
  MdOutlineEmail,
  MdInfoOutline,
} from "react-icons/md";
import { FaDownload } from "react-icons/fa6";
import { LuShare2 } from "react-icons/lu";
import Loader from "../../../../components/Loader.jsx";
import { FaCalendarAlt } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const clusterFilterSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 6,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    boxShadow: state.isFocused ? "0 0 0 1px #9ca3af" : "none",
    "&:hover": {
      borderColor: "#9ca3af",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 8px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#111827",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#d1d5db"
      : state.isFocused
      ? "#f3f4f6"
      : "#ffffff",
    color: "#111827",
    ":active": {
      backgroundColor: "#d1d5db",
    },
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 30,
  }),
};

/** Normalize stored LeetCode handle (URL or plain) for API / map keys. */
function pickValidLeetcodeUsername(raw) {
  const extracted = extractLeetCodeUsername(String(raw ?? ""));
  if (!extracted) return null;
  const t = extracted.trim();
  if (t.length < 3 || t.length > 40) return null;
  return t;
}

function displayLeetcodeUsername(raw) {
  const picked = pickValidLeetcodeUsername(raw);
  if (picked) return picked;
  const s = raw != null ? String(raw).trim() : "";
  return s || "-";
}

function mergeLeetcodeUsernames(leaderboardRows, clusterStudents) {
  const set = new Set();
  for (const row of leaderboardRows || []) {
    const u = pickValidLeetcodeUsername(row?.student?.leetCodeUname);
    if (u) set.add(u);
  }
  for (const st of clusterStudents || []) {
    const u = pickValidLeetcodeUsername(st?.leetCodeUname);
    if (u) set.add(u);
  }
  return [...set];
}

/** username (lowercase) -> totalSolved from GetLeetcodeBulk */
function leetcodeBulkRowsToTotalMap(rows) {
  const out = {};
  for (const row of rows || []) {
    const key = String(row?.username ?? "").trim().toLowerCase();
    if (!key) continue;
    const n = row?.totalSolved;
    out[key] = typeof n === "number" && !Number.isNaN(n) ? n : Number(n) || 0;
  }
  return out;
}

function leetcodeTotalFromLeaderboardLeetcodeData(student) {
  let leetcodeEasy = 0;
  let leetcodeMedium = 0;
  let leetcodeHard = 0;
  if (student.leetcode_data && student.leetcode_data.length > 0) {
    leetcodeEasy = student.leetcode_data.find((data) => data.difficulty === "Easy")?.count || 0;
    leetcodeMedium = student.leetcode_data.find((data) => data.difficulty === "Medium")?.count || 0;
    leetcodeHard = student.leetcode_data.find((data) => data.difficulty === "Hard")?.count || 0;
  }
  return leetcodeEasy + leetcodeMedium + leetcodeHard;
}

/** How long a fetched LeetCode dataset stays valid for a given filter/date-range before re-fetching. */
const LEETCODE_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
const LEADERBOARD_PAGE_SIZE = 20;
/** Number of skeleton placeholder rows shown while the next leaderboard page is loading. */
const NEXT_PAGE_SKELETON_ROWS = 6;

const ClusterReport = () => {
  const { campusName, clusterId } = useParams();
  const [filter, setFilter] = useState("weekly");
  const [showEmailDropdown, setShowEmailDropdown] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [clusterPdfLoading, setClusterPdfLoading] = useState(false);
  const [emails, setEmails] = useState("");
  const [emailError, setEmailError] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState();
  const [openLeetcodeModal, setOpenLeetcodeModal] = useState(false);
  const [leetcodeData, setLeetCodeData] = useState();
  const [customDateRange, setCustomDateRange] = useState({ startDate: null, endDate: null });
  const [showCalendar, setShowCalendar] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [customFilterLoading, setcustomFilterLoading] = useState(false);
  const [hoveredCourseIndex, setHoveredCourseIndex] = useState(null);
  const [Isload, setIsload] = useState(true);
  const [currentLeaderboardPage, setCurrentLeaderboardPage] = useState(1);
  const [hasMoreLeaderboard, setHasMoreLeaderboard] = useState(true);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  /** When true, show LeetCode column / row actions (after user opts in and refetch completes). */
  const [leetcodeTableEnabled, setLeetcodeTableEnabled] = useState(false);
  const [leetcodeFetchLoading, setLeetcodeFetchLoading] = useState(false);
  const [loadLeetcodeError, setLoadLeetcodeError] = useState(null);
  const [leetcodeToggleInfoOpen, setLeetcodeToggleInfoOpen] = useState(false);
  /** Lowercase LeetCode username -> totalSolved from GetLeetcodeBulk (table column source). */
  const [leetcodeBulkTotals, setLeetcodeBulkTotals] = useState({});
  /** Student id -> totalSolved when roster has leetCodeUname (covers leaderboard rows missing leetCodeUname). */
  const [leetcodeBulkByStudentId, setLeetcodeBulkByStudentId] = useState({});
  const lastLeaderboardVariablesRef = useRef({
    campusName,
    clusterId,
    filter: "weekly",
    startDate: null,
    endDate: null,
  });
  const leetcodeInfoPopoverRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  /** Bumped to invalidate an in-flight LeetCode load (e.g. user switches back to Course only while loading). */
  const leetcodeLoadGenerationRef = useRef(0);
  /** Caches the last successfully fetched LeetCode dataset, keyed by filter/date-range, with a timestamp for TTL expiry. */
  const leetcodeCacheRef = useRef({
    key: null,
    timestamp: 0,
    totals: {},
    byStudentId: {},
  });

  useEffect(() => {
    setTimeout(() => {
      setIsload(false);
    }, 2000);
  }, []);

  useEffect(() => {
    setLeetcodeTableEnabled(false);
    setLoadLeetcodeError(null);
    setLeetcodeToggleInfoOpen(false);
    // Note: we intentionally do NOT clear leetcodeBulkTotals/leetcodeBulkByStudentId
    // or leetcodeCacheRef here. loadLeetcodeForTable() decides freshness based on the
    // cache key (which is derived from filter/date-range) plus the 30-min TTL.
  }, [campusName, clusterId, filter, customDateRange.startDate, customDateRange.endDate]);

  useEffect(() => {
    if (!leetcodeToggleInfoOpen) return;
    const close = () => setLeetcodeToggleInfoOpen(false);
    const onPointerDown = (e) => {
      if (
        leetcodeInfoPopoverRef.current &&
        !leetcodeInfoPopoverRef.current.contains(e.target)
      ) {
        close();
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [leetcodeToggleInfoOpen]);

  const buildLeaderboardVariables = useCallback(() => {
    if (filter === "tilldate") {
      return {
        campusName,
        clusterId,
        filter: null,
        startDate: null,
        endDate: new Date().toISOString(),
      };
    }
    if (filter === "custom") {
      if (!customDateRange.startDate || !customDateRange.endDate) {
        return lastLeaderboardVariablesRef.current;
      }
      const endOfDay = new Date(
        new Date(customDateRange.endDate).setHours(23, 59, 59, 999)
      ).toISOString();
      return {
        campusName,
        clusterId,
        filter: null,
        startDate: customDateRange.startDate.toISOString(),
        endDate: endOfDay,
      };
    }
    return {
      campusName,
      clusterId,
      filter,
      startDate: null,
      endDate: null,
    };
  }, [campusName, clusterId, filter, customDateRange.startDate, customDateRange.endDate]);

  /** Stable cache key representing "which date range" the LeetCode data was fetched for. */
  const getLeetcodeCacheKey = useCallback(() => {
    const v = buildLeaderboardVariables();
    return `${campusName ?? ""}|${clusterId ?? ""}|${v.filter ?? ""}|${v.startDate ?? ""}|${v.endDate ?? ""}`;
  }, [buildLeaderboardVariables, campusName, clusterId]);

  const {
    data: ClusterData,
    loading: clusterLoading,
    error: clusterError,
  } = useGetClusterQuery({
    variables: { campusName, clusterId },
  });

  const {
    data: coursesData,
    loading: coursesLoading,
    error: coursesError,
  } = useGetAllCoursesByClusterQuery({
    variables: { campusName, clusterId },
  });

  const {
    data: leaderboardQueryData,
    loading: leaderboardLoading,
    error: leaderboardError,
    refetch: refetchLeaderboard,
    fetchMore: fetchMoreLeaderboard,
  } = useGetLeaderboardQuery({
    variables: {
      campusName,
      clusterId,
      filter,
      page: 1,
      limit: LEADERBOARD_PAGE_SIZE,
      startDate: null,
      endDate: null,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [fetchClusterStudents] = useGetClusterStudentsLazyQuery({
    fetchPolicy: "network-only",
  });

  const [fetchLeetcodeBulk] = useGetLeetcodeBulkLazyQuery({
    fetchPolicy: "network-only",
  });

  const [sendEmail, { loading }] = useSendEmailMutation();

  const refetchLeaderboardWithVariables = useCallback(
    async (variables) => {
      lastLeaderboardVariablesRef.current = variables;
      const result = await refetchLeaderboard({
        ...variables,
        page: 1,
        limit: LEADERBOARD_PAGE_SIZE,
      });
      if (result.error) throw result.error;
      if (result.errors?.length) {
        throw new Error(result.errors.map((e) => e.message).join(", "));
      }
      const rows = result?.data?.getLeaderboard ?? [];
      setCurrentLeaderboardPage(1);
      setHasMoreLeaderboard(rows.length === LEADERBOARD_PAGE_SIZE);
      return result;
    },
    [refetchLeaderboard]
  );

  const loadNextLeaderboardPage = useCallback(async () => {
    if (isFetchingNextPage || !hasMoreLeaderboard) return;
    setIsFetchingNextPage(true);
    const nextPage = currentLeaderboardPage + 1;
    const base = buildLeaderboardVariables();
    try {
      let incomingCount = 0;
      let appendedCount = 0;
      const result = await fetchMoreLeaderboard({
        variables: {
          ...base,
          page: nextPage,
          limit: LEADERBOARD_PAGE_SIZE,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.getLeaderboard?.length) return prev;

          const existing = prev?.getLeaderboard ?? [];
          const incoming = fetchMoreResult.getLeaderboard;
          incomingCount = incoming.length;
          const seen = new Set(
            existing.map((row) => String(row?.id ?? row?.student_id ?? ""))
          );
          const uniqueIncoming = incoming.filter((row) => {
            const key = String(row?.id ?? row?.student_id ?? "");
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          appendedCount = uniqueIncoming.length;

          return {
            ...prev,
            getLeaderboard: [...existing, ...uniqueIncoming],
          };
        },
      });

      if (appendedCount > 0) {
        setCurrentLeaderboardPage(nextPage);
      }

      // Keep loading only when server returned a full page and we could append rows.
      // If page is ignored by backend (same rows repeated), appendedCount becomes 0 and stops safely.
      setHasMoreLeaderboard(incomingCount === LEADERBOARD_PAGE_SIZE && appendedCount > 0);
    } catch (error) {
      console.error("Error loading next leaderboard page:", error);
      toast.error(error?.message || "Failed to load more leaderboard rows.");
    } finally {
      setIsFetchingNextPage(false);
    }
  }, [
    isFetchingNextPage,
    hasMoreLeaderboard,
    currentLeaderboardPage,
    buildLeaderboardVariables,
    fetchMoreLeaderboard,
  ]);

  useEffect(() => {
    if (!loadMoreSentinelRef.current) return;
    if (!leaderboardQueryData?.getLeaderboard?.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (leaderboardLoading || customFilterLoading) return;
        void loadNextLeaderboardPage();
      },
      { root: null, rootMargin: "200px 0px", threshold: 0 }
    );

    observer.observe(loadMoreSentinelRef.current);
    return () => observer.disconnect();
  }, [
    loadNextLeaderboardPage,
    leaderboardLoading,
    customFilterLoading,
    leaderboardQueryData?.getLeaderboard?.length,
  ]);

  useEffect(() => {
    const onScroll = () => {
      if (isFetchingNextPage || !hasMoreLeaderboard) return;
      if (leaderboardLoading || customFilterLoading) return;
      const viewportBottom = window.scrollY + window.innerHeight;
      const pageBottom = document.documentElement.scrollHeight;
      if (pageBottom - viewportBottom <= 260) {
        void loadNextLeaderboardPage();
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [
    isFetchingNextPage,
    hasMoreLeaderboard,
    leaderboardLoading,
    customFilterLoading,
    loadNextLeaderboardPage,
  ]);

  const leaderboardData = leaderboardQueryData;
  const hasLeaderboardRows = (leaderboardData?.getLeaderboard?.length ?? 0) > 0;

  const includeLeetcodeColumn = leetcodeTableEnabled;
  const showLeetcodeColumnPlaceholder = includeLeetcodeColumn || leetcodeFetchLoading;
  const showLeetcodeColumnSkeleton = leetcodeFetchLoading && !includeLeetcodeColumn;

  const getLeetcodeTableTotal = useCallback(
    (student) => {
      const sid = student?.student?.id ?? student?.student_id;
      const sidKey = sid != null && sid !== "" ? String(sid) : null;
      if (sidKey && leetcodeBulkByStudentId[sidKey] != null) {
        const v = leetcodeBulkByStudentId[sidKey];
        return typeof v === "number" && !Number.isNaN(v) ? v : 0;
      }
      const u = pickValidLeetcodeUsername(student?.student?.leetCodeUname);
      if (!u) return 0;
      const n = leetcodeBulkTotals[u.toLowerCase()];
      return typeof n === "number" && !Number.isNaN(n) ? n : 0;
    },
    [leetcodeBulkTotals, leetcodeBulkByStudentId]
  );

  const exportRowLeetcodeTotal = useCallback(
    (student) =>
      leetcodeTableEnabled ? getLeetcodeTableTotal(student) : leetcodeTotalFromLeaderboardLeetcodeData(student),
    [leetcodeTableEnabled, getLeetcodeTableTotal]
  );

  const loadLeetcodeForTable = useCallback(async () => {
    // Serve from cache if we have fresh data (same filter/date-range, within TTL).
    const cacheKey = getLeetcodeCacheKey();
    const cache = leetcodeCacheRef.current;
    const isCacheFresh =
      cache.key === cacheKey && Date.now() - cache.timestamp < LEETCODE_CACHE_TTL_MS;

    if (isCacheFresh) {
      setLeetcodeBulkTotals(cache.totals);
      setLeetcodeBulkByStudentId(cache.byStudentId);
      setLeetcodeTableEnabled(true);
      setLoadLeetcodeError(null);
      return;
    }

    const generation = ++leetcodeLoadGenerationRef.current;
    const isStale = () => generation !== leetcodeLoadGenerationRef.current;

    setLeetcodeFetchLoading(true);
    setLoadLeetcodeError(null);
    try {
      let clusterStudentRows = [];
      try {
        const { data: csData, error: csErr } = await fetchClusterStudents({
          variables: { clusterId },
        });
        if (!csErr) clusterStudentRows = csData?.getClusterStudents ?? [];
      } catch (e) {
        console.warn("GetClusterStudents failed (continuing with leaderboard usernames)", e);
      }

      if (isStale()) return;

      const lbRows = leaderboardQueryData?.getLeaderboard ?? [];
      const usernames = mergeLeetcodeUsernames(lbRows, clusterStudentRows);

      let bulkTotals = {};
      if (usernames.length > 0 && !isStale()) {
        try {
          const bulkRes = await fetchLeetcodeBulk({ variables: { usernames } });
          if (isStale()) return;
          if (bulkRes.error) console.warn("GetLeetcodeBulk:", bulkRes.error);
          else if (bulkRes.data?.leetcodeBulk) {
            bulkTotals = leetcodeBulkRowsToTotalMap(bulkRes.data.leetcodeBulk);
          }
        } catch (e) {
          console.warn("GetLeetcodeBulk failed (table may still load)", e);
        }
      }
      if (isStale()) return;

      setLeetcodeBulkTotals(bulkTotals);
      const byStudentId = {};
      for (const st of clusterStudentRows) {
        const u = pickValidLeetcodeUsername(st?.leetCodeUname);
        if (!u || st?.id == null || st.id === "") continue;
        const t = bulkTotals[u.toLowerCase()];
        if (typeof t === "number" && !Number.isNaN(t))
          byStudentId[String(st.id)] = t;
      }
      for (const row of lbRows) {
        const rowSid = row?.student?.id ?? row?.student_id;
        if (rowSid == null || rowSid === "") continue;
        const key = String(rowSid);
        if (byStudentId[key] != null) continue;
        const u = pickValidLeetcodeUsername(row?.student?.leetCodeUname);
        if (!u) continue;
        const t = bulkTotals[u.toLowerCase()];
        if (typeof t === "number" && !Number.isNaN(t)) byStudentId[key] = t;
      }
      setLeetcodeBulkByStudentId(byStudentId);

      if (isStale()) return;

      // Save only the LeetCode dataset. The leaderboard response already contains
      // the course values for the selected date range and must remain unchanged.
      leetcodeCacheRef.current = {
        key: cacheKey,
        timestamp: Date.now(),
        totals: bulkTotals,
        byStudentId,
      };

      setLeetcodeTableEnabled(true);
    } catch (e) {
      if (isStale()) return;
      console.error(e);
      const msg = e?.message || "Failed to load LeetCode data";
      setLoadLeetcodeError(msg);
      toast.error(msg);
    } finally {
      setLeetcodeFetchLoading(false);
    }
  }, [
    buildLeaderboardVariables,
    getLeetcodeCacheKey,
    clusterId,
    fetchClusterStudents,
    fetchLeetcodeBulk,
    leaderboardQueryData,
  ]);

  const createdAt = ClusterData?.getCluster?.createdAt;
  const filterOptions = [
    { value: "weekly", label: "PAST 7 DAYS" },
    { value: "daily", label: "TODAY" },
    { value: "past10days", label: "PAST 10 DAYS" },
    { value: "monthly", label: "THIS MONTH" },
    { value: "tilldate", label: "TILL DATE" },
    { value: "custom", label: "CUSTOM DATE" },
  ];

  const handleFilter = async (value) => {
    setFilter(value);
    setCurrentLeaderboardPage(1);
    setHasMoreLeaderboard(true);
    if (value === "tilldate") {
      setCustomDateRange({ startDate: null, endDate: null });
      try {
        await refetchLeaderboardWithVariables({
          campusName,
          clusterId,
          filter: null,
          startDate: null,
          endDate: new Date().toISOString(),
        });
      } catch (error) {
        console.error("Error applying filter:", error);
      }
      return;
    }

    if (value !== "custom") {
      setCustomDateRange({ startDate: null, endDate: null });
      try {
        await refetchLeaderboardWithVariables({
          campusName,
          clusterId,
          filter: value,
          startDate: null,
          endDate: null,
        });
      } catch (error) {
        console.error("Error applying filter:", error);
      }
    }
  };

  const applyFilter = async () => {
    setcustomFilterLoading(true);
    setCurrentLeaderboardPage(1);
    setHasMoreLeaderboard(true);

    if (filter === "custom" && (!customDateRange.startDate || !customDateRange.endDate)) {
      alert("Please select both start and end dates.");
      setcustomFilterLoading(false);
      return;
    }

    try {
      const endOfDay = customDateRange.endDate
        ? new Date(new Date(customDateRange.endDate).setHours(23, 59, 59, 999)).toISOString()
        : null;

      await refetchLeaderboardWithVariables({
        campusName,
        clusterId,
        filter: filter === "custom" ? null : filter,
        startDate: filter === "custom" ? customDateRange.startDate?.toISOString() ?? null : null,
        endDate: filter === "custom" ? endOfDay : null,
      });
    } catch (error) {
      console.error("Error applying filter:", error);
    } finally {
      setcustomFilterLoading(false);
    }
  };

  /** CSV: per-course completed/total columns match the on-screen BT courses table.
   *  Email/Phone/LeetCode-username are kept here for export even though they're
   *  no longer shown as table columns on screen. */
  const buildClusterReportCsv = useCallback(() => {
    if (!leaderboardData?.getLeaderboard) return "";
    const courses = coursesData?.getAllCoursesByCluster ?? [];

    const baseHeaders = ["Email", "Name", "Department", "Points", "Course Last Submission"];
    const courseHeaders =
      courses.length > 0
        ? courses.map((c) => c.name?.trim() || "Course")
        : ["Course questions (aggregate)"];
    const leetHeaders = leetcodeTableEnabled ? ["Leetcode Questions Completed"] : [];
    const headerRow = [...baseHeaders, ...courseHeaders, ...leetHeaders].map(escapeCsvField).join(",");

    const dataRows = leaderboardData.getLeaderboard
      .map((student) => {
        const baseCells = [
          student.student.email,
          student.student.name && student.student.name.trim() !== "" ? student.student.name : "-",
          student.department && String(student.department).trim() !== "" ? student.department : "-",
          student.totalScore,
          student.latestSubmissionDate ? formatDate(student.latestSubmissionDate) : "-",
        ];
        const courseCells =
          courses.length > 0
            ? courses.map((course) => {
                const completed = getCourseCompleted(course, student);
                const total = getCourseTotalQuestions(course);
                const hasBreakdownData = hasCourseBreakdownData(course, student);
                return hasBreakdownData ? `${completed}/${total || "—"}` : String(completed);
              })
            : [String(student.completedQuestions ?? "-")];
        const leetCells = leetcodeTableEnabled ? [String(exportRowLeetcodeTotal(student))] : [];
        return [...baseCells, ...courseCells, ...leetCells].map(escapeCsvField).join(",");
      })
      .join("\n");
    return `${headerRow}\n${dataRows}`;
  }, [leaderboardData, coursesData, leetcodeTableEnabled, exportRowLeetcodeTotal]);

  const downloadCSV = () => {
    const csvContent = buildClusterReportCsv();
    if (!csvContent) return;

    const clusterName = ClusterData?.getCluster?.name || "Cluster";
    const currentDate = formatDate(new Date());

    jsFileDownload(csvContent, `${clusterName}_${currentDate}.csv`);
  };

  const handleClusterPdfDownload = useCallback(async () => {
    if (clusterPdfLoading) return;
    const rows = leaderboardData?.getLeaderboard;
    if (!rows?.length) {
      toast.error("No report data to export as PDF.");
      return;
    }
    setClusterPdfLoading(true);
    try {
      const blob = await pdf(
        <PDFDocument
          students={rows}
          courses={coursesData?.getAllCoursesByCluster ?? []}
          ClusterName={ClusterData?.getCluster?.name}
          CreatedAt={createdAt ? formatDate(createdAt) : "-"}
          includeLeetcodeColumn={leetcodeTableEnabled}
          leetcodeBulkTotals={leetcodeTableEnabled ? leetcodeBulkTotals : undefined}
          leetcodeBulkByStudentId={leetcodeTableEnabled ? leetcodeBulkByStudentId : undefined}
        />
      ).toBlob();

      const safeBase = String(ClusterData?.getCluster?.name || "Cluster").replace(/[\\/:*?"<>|]+/g, "-").trim();
      const stamp = formatDate(new Date());
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${safeBase}_${stamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setShowDownloadDropdown(false);
    } catch (err) {
      console.error("Cluster PDF:", err);
      toast.error(err?.message || "Failed to generate PDF.");
    } finally {
      setClusterPdfLoading(false);
    }
  }, [
    clusterPdfLoading,
    leaderboardData?.getLeaderboard,
    ClusterData?.getCluster?.name,
    createdAt,
    leetcodeTableEnabled,
    leetcodeBulkTotals,
    leetcodeBulkByStudentId,
    coursesData?.getAllCoursesByCluster,
  ]);

  const parseEmailList = (raw) =>
    String(raw || "")
      .split(/[\s,;]+/)
      .map((email) => email.trim())
      .filter(Boolean);

  const validateEmails = (emailString) => {
    const emailArray = parseEmailList(emailString);
    if (emailArray.length === 0) return false;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailArray.every((email) => emailPattern.test(email));
  };

  const handleSendEmail = async () => {
    if (!validateEmails(emails)) {
      setEmailError(
        "Enter one or more valid emails, separated by commas, spaces, or newlines."
      );
      return;
    }
    setEmailError("");

    const emailList = parseEmailList(emails);

    const csvContent = buildClusterReportCsv();
    if (!csvContent) {
      toast.error("No report data to send.");
      return;
    }

    try {
      await sendEmail({
        variables: {
          emailInput: {
            emailList,
            csvContent,
            assignmentName: ClusterData?.getCluster.name,
          },
        },
      });
      toast.success("Email sent successfully.");
      setShowEmailDropdown(false);
      setEmails("");
    } catch (error) {
      toast.error(error.message || "Failed to send email.");
    }
  };

  /** Leaderboard refetch during +LeetCode load would set `leaderboardLoading` and flash the full-page loader; keep table + column skeletons visible instead. */
  const hideGlobalLoaderForLeetcodeRefetch =
    leetcodeFetchLoading && leaderboardQueryData != null;

  if (
    clusterLoading ||
    coursesLoading ||
    Isload ||
    (leaderboardLoading && !hideGlobalLoaderForLeetcodeRefetch && !hasLeaderboardRows)
  ) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader />
        </div>
      </AdminLayout>
    );
  }

  if (clusterError || leaderboardError || coursesError) {
    return (
      <AdminLayout>
        <div className="p-10">
          <Alert color="failure">
            <span>
              <p>
                Error fetching data:{" "}
                {clusterError?.message || leaderboardError?.message || coursesError?.message}
              </p>
            </span>
          </Alert>
        </div>
      </AdminLayout>
    );
  }

  const clusterCourses = coursesData?.getAllCoursesByCluster || [];
  const tableCourses = clusterCourses.length
    ? clusterCourses
    : [{ id: "placeholder", name: "Course" }];

  return (
    <AdminLayout>
      <div className="">
        <div className="w-full min-h-screen pb-8 mt-2 bg-white rounded-lg shadow-lg">
          <div className="flex flex-col items-center justify-between p-5 border-b md:flex-row">
            <div className="flex flex-col">
              <h2 className="text-2xl font-bold">
                {ClusterData?.getCluster?.name || "Cluster"} Overview
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                Created At: {formatDate(createdAt)}
              </p>
            </div>
            <h4 className="text-lg">
              <span className="font-semibold">Students count:</span>{" "}
              {ClusterData?.getCluster?.studentCount || 0}
            </h4>
          </div>
          <div className="p-5">
            <div className="flex flex-wrap items-end justify-between mb-5 gap-x-4 gap-y-4 max-md:flex-col">
              <div className="w-full md:w-[180px]">
                <p className="mb-1 text-sm font-semibold text-gray-600">Date filter</p>
                <ReactSelect
                  inputId="filter"
                  isSearchable={false}
                  options={filterOptions}
                  value={filterOptions.find((option) => option.value === filter) || filterOptions[0]}
                  onChange={(option) => handleFilter(option?.value || "weekly")}
                  styles={clusterFilterSelectStyles}
                />
              </div>
              {filter === "custom" && (
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveField('start');
                        setShowCalendar((s) => activeField !== 'start' ? true : !s);
                      }}
                      className={`flex items-center gap-2 w-44 h-11 px-4 py-2 text-sm bg-white border rounded-2xl shadow-sm focus:outline-none transition-all
                        ${customDateRange.startDate ? 'border-blue-500' : 'border-gray-300'}
                        hover:shadow-md focus:ring-2 focus:ring-blue-500`}
                    >
                      <FaCalendarAlt className="text-gray-500" />
                      <span className={`${customDateRange.startDate ? 'text-gray-800' : 'text-gray-400'}`}>
                        {customDateRange.startDate
                          ? new Date(customDateRange.startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                          : 'Start date'}
                      </span>
                    </button>
                    {showCalendar && activeField === 'start' && (
                      <div className="absolute right-0 z-40 p-4 mt-2 bg-white border rounded-xl shadow-xl w-[320px]">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-semibold text-gray-700">Select start date</span>
                          <button
                            onClick={() => setShowCalendar(false)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >✕</button>
                        </div>
                        <DatePicker
                          selected={customDateRange.startDate}
                          onChange={(date) => {
                            setCustomDateRange((prev) => ({ ...prev, startDate: date || null }));
                            if (date) setShowCalendar(false);
                          }}
                          inline
                          calendarClassName="bt-calendar"
                        />
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveField('end');
                        setShowCalendar((s) => activeField !== 'end' ? true : !s);
                      }}
                      className={`flex items-center gap-2 w-44 h-11 px-4 py-2 text-sm bg-white border rounded-2xl shadow-sm focus:outline-none transition-all
                        ${customDateRange.endDate ? 'border-blue-500' : 'border-gray-300'}
                        hover:shadow-md focus:ring-2 focus:ring-blue-500`}
                    >
                      <FaCalendarAlt className="text-gray-500" />
                      <span className={`${customDateRange.endDate ? 'text-gray-800' : 'text-gray-400'}`}>
                        {customDateRange.endDate
                          ? new Date(customDateRange.endDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                          : 'End date'}
                      </span>
                    </button>
                    {showCalendar && activeField === 'end' && (
                      <div className="absolute right-0 z-40 p-4 mt-2 bg-white border rounded-xl shadow-xl w-[320px]">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-semibold text-gray-700">Select end date</span>
                          <button
                            onClick={() => setShowCalendar(false)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >✕</button>
                        </div>
                        <DatePicker
                          selected={customDateRange.endDate}
                          onChange={(date) => {
                            setCustomDateRange((prev) => ({ ...prev, endDate: date || null }));
                            if (date) setShowCalendar(false);
                          }}
                          inline
                          calendarClassName="bt-calendar"
                          minDate={customDateRange.startDate}
                        />
                      </div>
                    )}
                  </div>
                  <Button onClick={applyFilter} isProcessing={customFilterLoading}>
                    Apply
                  </Button>
                </div>
              )}

              <div className="relative flex flex-col items-stretch w-full gap-3 mt-4 md:mt-0 md:ml-auto md:w-auto md:flex-row md:items-center md:justify-end md:gap-4">
                <div className="flex flex-col w-full gap-1 md:w-auto md:items-start">
                  <p className="mb-0.5 text-sm font-semibold text-gray-600">Show LeetCode Progress</p>
                  <div
                    role="group"
                    aria-label="Include LeetCode columns in the table"
                    className="inline-flex w-full min-w-0 items-stretch rounded-2xl border border-slate-300/60 bg-slate-200/55 p-1 shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)] md:w-auto md:min-w-[11.5rem]"
                  >
                    <button
                      type="button"
                      aria-pressed={!includeLeetcodeColumn && !leetcodeFetchLoading}
                      onClick={() => {
                        if (!includeLeetcodeColumn) {
                          if (leetcodeFetchLoading) {
                            leetcodeLoadGenerationRef.current += 1;
                            setLeetcodeFetchLoading(false);
                            setLoadLeetcodeError(null);
                          }
                          return;
                        }
                        setLeetcodeTableEnabled(false);
                        setLoadLeetcodeError(null);
                        // Note: leetcodeBulkTotals/leetcodeBulkByStudentId and leetcodeCacheRef
                        // are intentionally left untouched so re-enabling within the TTL is instant.
                      }}
                      className={`flex flex-1 items-center justify-center rounded-xl px-2.5 py-1.5 text-center text-[11px] font-semibold leading-none tracking-tight transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5c7a94]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white md:flex-none md:min-w-[4.75rem] ${
                        !includeLeetcodeColumn && !leetcodeFetchLoading
                          ? "bg-[#5c7a94] text-white shadow-[0_4px_14px_rgba(60,90,120,0.45)] ring-2 ring-white/35"
                          : "text-slate-600 hover:bg-white/55 hover:text-slate-900"
                      }`}
                    >
                      Disable
                    </button>
                    <button
                      type="button"
                      aria-pressed={includeLeetcodeColumn || leetcodeFetchLoading}
                      disabled={leetcodeFetchLoading}
                      title={
                        leetcodeFetchLoading
                          ? "Loading each student's solved LeetCode total for this date range."
                          : includeLeetcodeColumn
                            ? "Solved LeetCode totals are shown for this date range."
                            : "Turn on to load each student's solved LeetCode total for this date range."
                      }
                      onClick={() => {
                        if (includeLeetcodeColumn) return;
                        void loadLeetcodeForTable();
                      }}
                      className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl px-2.5 py-1.5 text-center text-[11px] font-semibold leading-none tracking-tight transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5c7a94]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-wait md:flex-none md:min-w-[4.75rem] ${
                        includeLeetcodeColumn || leetcodeFetchLoading
                          ? "bg-[#5c7a94] text-white shadow-[0_4px_14px_rgba(60,90,120,0.45)] ring-2 ring-white/35"
                          : "text-slate-600 hover:bg-white/55 hover:text-slate-900"
                      }`}
                    >
                      <span aria-busy={leetcodeFetchLoading}>Enable</span>
                    </button>
                    <span
                      ref={leetcodeInfoPopoverRef}
                      className="relative self-center shrink-0"
                    >
                      <button
                        type="button"
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-white/55 hover:text-[#5c7a94] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5c7a94]/40 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent"
                        aria-label="Learn what the LeetCode option does"
                        aria-expanded={leetcodeToggleInfoOpen}
                        onClick={(e) => {
                          e.stopPropagation();
                          setLeetcodeToggleInfoOpen((o) => !o);
                        }}
                      >
                        <MdInfoOutline className="h-[17px] w-[17px]" aria-hidden />
                      </button>
                      {leetcodeToggleInfoOpen && (
                        <div
                          role="tooltip"
                          className="absolute bottom-full left-1/2 z-[100] mb-1.5 w-[min(15rem,calc(100vw-2rem))] -translate-x-1/2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left shadow-lg"
                        >
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            + LeetCode
                          </p>
                          <p className="mt-1 text-xs leading-snug text-slate-600">
                            Enabling this loads each student&apos;s total solved LeetCode problems.
                          </p>
                        </div>
                      )}
                    </span>
                  </div>
                  {loadLeetcodeError && (
                    <p className="text-right text-[11px] font-medium text-red-600">{loadLeetcodeError}</p>
                  )}
                </div>
                <div className="relative flex flex-row flex-wrap items-center self-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDownloadDropdown((prev) => !prev);
                    setShowEmailDropdown(false);
                  }}
                  title="Download"
                  aria-label="Download"
                  className="flex items-center justify-center w-10 h-10 mt-5 transition-all group text-slate-600 hover:text-slate-900 focus:outline-none"
                >
                  <FaDownload size={19} />
                </button>

                {showDownloadDropdown && (
                  <div className="absolute top-0 right-[calc(100%+10px)] z-20 w-[165px] sm:w-[180px] rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_10px_24px_rgba(2,6,23,0.08)]">
                    <p className="mb-2 px-2 text-[11px] font-semibold tracking-wide text-slate-500 uppercase">Choose format</p>
                    <button
                      onClick={() => {
                        downloadCSV();
                        setShowDownloadDropdown(false);
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-slate-700 transition-colors hover:bg-slate-100"
                    >
                      <span className="rounded-lg bg-slate-100 p-1.5 text-slate-600">
                        <MdTableChart size={17} />
                      </span>
                      <span className="text-sm font-medium">CSV</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleClusterPdfDownload()}
                      className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors ${
                        clusterPdfLoading ? "text-slate-400" : "text-slate-700 hover:bg-slate-100"
                      }`}
                      disabled={clusterPdfLoading}
                    >
                      <span
                        className={`rounded-lg p-1.5 ${
                          clusterPdfLoading ? "bg-slate-100 text-slate-400" : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        <MdPictureAsPdf size={17} />
                      </span>
                      <span className="text-sm font-medium">PDF</span>
                    </button>
                  </div>
                )}

                <button
                  onClick={() => {
                    setShowEmailDropdown(!showEmailDropdown);
                    setShowDownloadDropdown(false);
                  }}
                  title="Share"
                  aria-label="Share"
                  className="flex items-center justify-center w-10 h-10 mt-5 transition-all group text-slate-600 hover:text-slate-900 focus:outline-none"
                >
                  <LuShare2 size={20} />
                </button>

                {showEmailDropdown && (
                  <div className="absolute right-0 top-[50px] z-10 mt-2 w-full rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_10px_24px_rgba(2,6,23,0.08)] sm:w-96">
                    <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                      <MdOutlineEmail size={16} />
                      Share via email
                    </p>
                    <textarea
                      className="w-full p-3 text-sm transition border outline-none rounded-xl border-slate-300 text-slate-700 focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
                      placeholder="Emails — comma, space, or one per line"
                      value={emails}
                      onChange={(e) => setEmails(e.target.value)}
                    />
                    {emailError && (
                      <p className="mt-2 text-sm text-red-500">{emailError}</p>
                    )}
                    <button
                      disabled={loading}
                      onClick={handleSendEmail}
                      className="mt-3 inline-flex w-full items-center justify-center rounded-xl bg-gray-500 px-4 py-2.5 text-center text-sm font-medium text-white transition hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
                    >
                      {loading && <Spinner size={"sm"}  className="mb-1 mr-2"/>}Send Now
                    </button>
                  </div>
                )}
                </div>
              </div>
            </div>
            <div className="mt-5 overflow-auto border shadow-sm rounded-xl border-slate-200 max-h-[calc(100vh-280px)]">
              {!leaderboardData?.getLeaderboard?.length ? (
                <p className="p-5 text-sm text-gray-500">No data available for the selected filter.</p>
              ) : (
                <>
                  <Table className="min-w-full bg-white">
                    <thead className="sticky top-0 z-10 bg-white">
                      <tr>
                        {/* Structural fix only: this group now spans S.No + Roll No + Name + Department (4 cols)
                            since Email/Phone/LeetCode-username were removed. The "Better Tomorrow
                            Courses" grouping row itself is left untouched. */}
                        <th aria-hidden="true" colSpan={4} className="px-2 py-2 bg-white sm:px-3" />
                        <th scope="colgroup" colSpan={tableCourses.length} className="px-2 py-2 bg-white sm:px-3">
                          <div className="flex items-center w-full gap-3">
                            <span className="flex-1 h-px rounded-full bg-slate-300" />
                            <span className="text-[11px] font-extrabold uppercase tracking-widest text-[#4a6278] sm:text-xs">
                              Better Tomorrow Courses
                            </span>
                            <span className="flex-1 h-px rounded-full bg-slate-300" />
                          </div>
                        </th>
                        {showLeetcodeColumnPlaceholder && (
                          <th aria-hidden="true" className="px-2 py-2 bg-white sm:px-3" />
                        )}
                        <th aria-hidden="true" colSpan={3} className="px-2 py-2 bg-white sm:px-3" />
                      </tr>
                      <tr style={{ backgroundColor: "#5c7a94" }}>
                        <th className="px-2 py-1.5 text-center text-[10px] text-white sm:px-3 sm:py-2 sm:text-xs">
                          S.No
                        </th>
                        <th className="px-2 py-1.5 text-center text-[10px] text-white sm:px-3 sm:py-2 sm:text-xs">
                          Roll No
                        </th>
                        <th className="px-2 py-1.5 text-center text-[10px] text-white sm:px-3 sm:py-2 sm:text-xs">
                          Name
                        </th>
                        <th className="px-2 py-1.5 text-center text-[10px] text-white sm:px-3 sm:py-2 sm:text-xs">
                          Department
                        </th>
                        {tableCourses.map((course, courseIndex) => (
                          <th
                            key={course.id || course.name}
                            title={course.name}
                            onMouseEnter={() => setHoveredCourseIndex(courseIndex)}
                            onMouseLeave={() => setHoveredCourseIndex(null)}
                            className={`px-2 py-2 text-center text-[11px] text-white sm:px-3 sm:py-2 sm:text-sm transition-colors duration-200 max-w-[160px] ${
                              hoveredCourseIndex === courseIndex ? "bg-[#4d6880]" : ""
                            }`}
                          >
                            <span className="flex items-center justify-center font-semibold leading-snug">
                              <span className="inline-flex items-center justify-center gap-0.5">
                                <span className="text-white/70">[</span>
                                <span className="tracking-wide break-words">{course.name}</span>
                                <span className="text-white/70">]</span>
                              </span>
                            </span>
                          </th>
                        ))}
                        {showLeetcodeColumnPlaceholder && (
                          <th className="px-2 py-1.5 text-center text-[10px] text-white sm:px-3 sm:py-2 sm:text-xs">
                            Leetcode Questions <br />
                            Completed
                          </th>
                        )}
                        <th className="px-2 py-1.5 text-center text-[10px] text-white sm:px-3 sm:py-2 sm:text-xs">
                          Course Last <br />
                          Submission
                        </th>
                        <th className="px-2 py-1.5 text-center text-[10px] text-white sm:px-3 sm:py-2 sm:text-xs">
                          Earned Point
                        </th>
                        <th className="px-2 py-1.5 text-center text-[10px] text-white sm:px-3 sm:py-2 sm:text-xs">
                          View More
                        </th>
                      </tr>
                    </thead>
                    <TableBody>
                      {leaderboardData?.getLeaderboard.map((student, index) => {
                        const isZebra = index % 2 === 1;
                        const serialNo = index + 1;
                        const rollNo = student?.student?.rollNo && String(student.student.rollNo).trim() !== ""
                          ? student.student.rollNo
                          : "-";
                        return (
                          <TableRow
                            key={index}
                            className={`${isZebra ? "bg-slate-50/60" : "bg-white"} transition-colors hover:bg-slate-100/70`}
                          >
                            <TableCell className="px-4 py-3 text-sm font-medium text-center text-gray-900 border-b border-gray-100">
                              {serialNo}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-sm text-center text-gray-700 border-b border-gray-100">
                              {rollNo}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-sm font-medium text-center text-gray-900 border-b border-gray-100">
                              {student.student.name && student.student.name.trim() !== '' ? student.student.name : '-'}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-sm text-center text-gray-700 border-b border-gray-100">
                              {student.department && String(student.department).trim() !== '' ? student.department : '-'}
                            </TableCell>
                            {tableCourses.map((course, courseIndex) => {
                              const completed = getCourseCompleted(course, student);
                              return (
                                <TableCell
                                  key={course.id || course.name}
                                  className={`px-4 py-3 text-center border-b border-gray-100 transition-colors duration-200 ${
                                    hoveredCourseIndex === courseIndex
                                      ? "bg-[#f3f6f9]"
                                      : "bg-[#f8fbff]"
                                  }`}
                                  style={{
                                    position: "relative",
                                    overflow: "hidden",
                                    boxShadow:
                                      hoveredCourseIndex === courseIndex
                                        ? "inset 0 0 0 1px #d6dee6, inset 0 -2px 0 #c7d1dc"
                                        : "none",
                                  }}
                                >
                                  <span className="inline-block text-sm font-semibold text-gray-800 bt-course-value">
                                      {completed}
                                  </span>
                                </TableCell>
                              );
                            })}
                            {showLeetcodeColumnPlaceholder && (
                            <TableCell className="px-4 py-3 text-center border-b border-gray-100">
                              {showLeetcodeColumnSkeleton ? (
                                <div
                                  className="mx-auto h-4 w-12 max-w-[80%] animate-pulse rounded-md bg-slate-200/90"
                                  aria-hidden
                                />
                              ) : (
                                <span className="text-sm text-gray-700">{getLeetcodeTableTotal(student)}</span>
                              )}
                            </TableCell>
                            )}
                            <TableCell className="px-4 py-3 text-sm text-center text-gray-700 border-b border-gray-100">
                              {student.latestSubmissionDate
                                ? formatDate(student.latestSubmissionDate)
                                : "-"}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-sm font-semibold text-center text-gray-900 border-b border-gray-100">
                              {student.totalScore}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-center align-middle border-b border-gray-100">
                              <Dropdown
                                arrowIcon={false}
                                inline={true}
                                className="w-44"
                                placement="bottom"
                                label={<BsThreeDots className="text-gray-500" />}
                              >
                                <Dropdown.Item
                                  onClick={() => {
                                    setOpenModal(true);
                                    setSelectedStudent(student.student);
                                  }}
                                >
                                  Course Details
                                </Dropdown.Item>
                                {includeLeetcodeColumn && (
                                <Dropdown.Item
                                  onClick={() => {
                                    setOpenLeetcodeModal(true);
                                    setLeetCodeData(student.leetcode_data);
                                  }}
                                >
                                  LeetCode
                                </Dropdown.Item>
                                )}
                              </Dropdown>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {/* Skeleton placeholder rows shown while the next 20 leaderboard rows load. */}
                      {isFetchingNextPage &&
                        Array.from({ length: NEXT_PAGE_SKELETON_ROWS }).map((_, skeletonIndex) => (
                          <TableRow
                            key={`skeleton-${skeletonIndex}`}
                            className={`${skeletonIndex % 2 === 1 ? "bg-slate-50/60" : "bg-white"} animate-pulse`}
                          >
                            <TableCell className="px-4 py-3 border-b border-gray-100">
                              <div className="mx-auto h-3.5 w-8 rounded bg-slate-200" />
                            </TableCell>
                            <TableCell className="px-4 py-3 border-b border-gray-100">
                              <div className="mx-auto h-3.5 w-16 rounded bg-slate-200" />
                            </TableCell>
                            <TableCell className="px-4 py-3 border-b border-gray-100">
                              <div className="mx-auto h-3.5 w-24 rounded bg-slate-200" />
                            </TableCell>
                            <TableCell className="px-4 py-3 border-b border-gray-100">
                              <div className="mx-auto h-3.5 w-20 rounded bg-slate-200" />
                            </TableCell>
                            {tableCourses.map((course, courseIndex) => (
                              <TableCell
                                key={`skeleton-course-${course.id || course.name}-${skeletonIndex}`}
                                className="px-4 py-3 text-center border-b border-gray-100 bg-[#f8fbff]"
                              >
                                <div className="mx-auto h-3.5 w-8 rounded bg-slate-200" />
                              </TableCell>
                            ))}
                            {showLeetcodeColumnPlaceholder && (
                              <TableCell className="px-4 py-3 border-b border-gray-100">
                                <div className="mx-auto h-3.5 w-10 rounded bg-slate-200" />
                              </TableCell>
                            )}
                            <TableCell className="px-4 py-3 border-b border-gray-100">
                              <div className="mx-auto h-3.5 w-16 rounded bg-slate-200" />
                            </TableCell>
                            <TableCell className="px-4 py-3 border-b border-gray-100">
                              <div className="mx-auto h-3.5 w-10 rounded bg-slate-200" />
                            </TableCell>
                            <TableCell className="px-4 py-3 border-b border-gray-100">
                              <div className="w-4 h-4 mx-auto rounded bg-slate-200" />
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  {!hasMoreLeaderboard && hasLeaderboardRows && !isFetchingNextPage && (
                    <p className="py-2.5 text-center text-xs text-gray-400 border-t border-gray-100">No more students to load.</p>
                  )}
                  <div ref={loadMoreSentinelRef} className="w-full h-2" aria-hidden="true" />

                  <Modal
                    show={openLeetcodeModal}
                    onClose={() => setOpenLeetcodeModal(false)}
                    className="w-6xl"
                  >
                    {" "}
                    {/* Custom width */}
                    <Modal.Header>LeetCode Progress</Modal.Header>
                    <Modal.Body>
                      <AcSubmissionChart
                        isLoading={leaderboardLoading}
                        acSubmissionNum={leetcodeData}
                      />
                    </Modal.Body>
                  </Modal>

                  {/* Modal placed outside the loop */}
                  <Modal
                    show={openModal}
                    onClose={() => setOpenModal(false)}
                    className="w-6xl"
                  >
                    {" "}
                    {/* Custom width */}
                    <Modal.Header>Overall Course Progress</Modal.Header>
                    <Modal.Body>
                      <StudentCourseProgression
                        studentData={selectedStudent}
                        isAdmin={true}
                        paidDashboard={true}
                      />
                      {!selectedStudent && (
                        <Overlay message="Course progress data not available" />
                      )}
                    </Modal.Body>
                  </Modal>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ClusterReport;